import { ComparisonTable } from '@elearning-platform/comparisonTable';
import { Icon, IconName, UseState } from '@elearning-platform/ui';
import React, { useEffect, useState } from 'react';

import { AppConfig, Theme } from '../../appConfig';
import { useAuth, useBodyScrollLock, useVolatileSettings } from '../../hooks';
import { UseAuth, UseVolatileSettings } from '../../interfaces';

import { CloseButton, PopupContainer } from './comparisonTablePopup.styled';
import { ComparisonTableData, ComparisonTableSourceData, ComparisonTableSourceMainConfig, TableColumn } from './comparisonTablePopup.types';

export const ComparisonTablePopup: React.FC = (): JSX.Element => {
  const { volatileSettings, changeComparisonTablePopupVisible }: UseVolatileSettings = useVolatileSettings();
  const [dataSources, setDataSources]: UseState<Record<string, ComparisonTableSourceData> | null>
    = useState<Record<string, ComparisonTableSourceData> | null>(null);
  const { isLoggedIn }: UseAuth = useAuth();
  useBodyScrollLock(volatileSettings.isComparisonTablePopupVisible);

  const close: () => void = (): void => changeComparisonTablePopupVisible(false);

  const downloadStructure: <T>(url: string) => Promise<T | undefined>
    = async <T,>(url: string): Promise<T | undefined> => fetch(`${AppConfig.content}${url}`, { credentials: 'include' })
      .then(async (response: Response): Promise<T> => {
        if (!response.ok) {
          throw new Error();
        }

        return response.json() as Promise<T>;
      })
      .catch((): undefined => undefined);

  useEffect(
    (): void => {
      if (!Theme.ComparisonTableEnabled || !isLoggedIn || dataSources || !volatileSettings.isComparisonTablePopupVisible) {
        return;
      }

      void downloadStructure<ComparisonTableSourceMainConfig>('/assets/files/porownanie-ofert-ubezpieczen-tabelka/config.json')
        .then(async (data: ComparisonTableSourceMainConfig | undefined): Promise<void> => {
          if (!data) {
            return;
          }
          const localDataSources: Record<string, ComparisonTableSourceData> = {};

          for (const item of Object.entries(data)) {
            const data: ComparisonTableData | undefined = await downloadStructure<ComparisonTableData>(item[1].sourceUrl);
            if (!data) {
              continue;
            }

            localDataSources[item[0]] = {
              ...item[1],
              icon: `${AppConfig.content}${item[1].icon}`,
              data: {
                ...data,
                columns: data.columns.map((column: TableColumn): TableColumn => ({
                  ...column,
                  ...(column.imageUrl ? { imageUrl: `${AppConfig.content}${column.imageUrl}` } : {}),
                })),
              },
            };
          }

          setDataSources(localDataSources);
        });
    },
    [dataSources, setDataSources, isLoggedIn, volatileSettings.isComparisonTablePopupVisible]
  );

  return (
    <PopupContainer $visible={volatileSettings.isComparisonTablePopupVisible}>
      <CloseButton onClick={close}>
        <Icon name={IconName.ClearCircleOutlined} size={40} />
      </CloseButton>
      {!!dataSources && (
        <ComparisonTable dataSources={dataSources} />
      )}
    </PopupContainer>
  );
};
